<script context="module">
  import { get, writable } from "svelte/store";
  import {
    CachedRenderer,
    CanvasPoolManager,
  } from "../util/fast-canvas-pool.js";

  const canvasPoolCache = CachedRenderer({ size: 64 });
  const DIMENSIONS = 768;
  const ITEMS_PER_PAGE = 64;

  let memory = {};

  function getStore(addressKey, order = "asc") {
    addressKey = addressKey || "root";
    const key = `${addressKey}:${order}`;
    if (!(key in memory)) {
      memory[key] = {
        order: order === "desc" ? "desc" : "asc",
        store: writable([]),
        offset: 0,
        scrollTop: null,
      };
    }
    return memory[key];
  }
</script>

<script>
  import StaticArtwork from "./StaticArtwork.svelte";
  import { getContext, onDestroy, onMount } from "svelte";
  import Button from "./Button.svelte";
  import {
    hexToEncoding,
    encodingToHex,
    createEmptyEncoding,
    renderToCanvas,
  } from "bitframes";
  import {
    fetchOpenEditionTokenCount,
    fetchTokens,
  } from "../util/data-fetcher.js";
  import {
    colorSpace,
    CONTRACT_NETWORK,
    getBackground,
  } from "../util/constants";
  import { shortAddress } from "../util/shortAddress";
  import { link } from "../util/anchor-link.js";
  import Footer from "./Footer.svelte";
  import UserLink from "./UserLink.svelte";
  import LinkButton from "./LinkButton.svelte";
  // import { windowState } from "../util/window-state.js";
  // import { createWorker, getWorker } from "../util/worker.pool.js";
  // import CanvasWorker from '../util/worker.renderer.js?url'
  // console.log(CanvasW)

  export let address = "";
  export let system = 0x00;

  const EMPTY_ENCODING_HEX = encodingToHex(createEmptyEncoding());

  const gallery = getContext("gallery");
  const compact = gallery.compact;
  const orderStore = gallery.order;

  let _lastAddress;
  let _lastOrder;

  let curSubscription;
  let curBackground;
  curBackground = getBackground({ system });
  $: curBackground = getBackground({ system });

  let curOrder;
  const orderStoreUnsub = orderStore.subscribe((v) => {
    curOrder = v;
  });

  let curMinResult = address ? 0 : 16;
  let curState;
  let curDataList;
  let isEmpty = false;
  $: {
    curState = getStore(address, curOrder);
    if (_lastAddress !== address || _lastOrder !== curOrder) {
      if (curSubscription) {
        curSubscription();
        curSubscription = null;
      }
      curSubscription = curState.store.subscribe((data) => {
        curDataList = data;
      });

      _lastAddress = address;
      _lastOrder = curOrder;
      firstRun = true;
      onChange(curState);
      isEmpty = false;
      visibleTokenIDs.clear();
    }
  }

  onDestroy(() => {
    if (curSubscription) curSubscription();
    if (orderStoreUnsub) orderStoreUnsub();
  });

  let loading = true;
  let curTokenCount = null;
  let firstRun = true;

  const visibleTokenIDs = new Set();
  let lastMinRange, lastMaxRange;
  let minRange = null,
    maxRange = null;

  const pool = CanvasPoolManager({
    hatch: true,
    hatchContours: true,
    maxPaintsPerFrame: 2,
    maxPerFrame: 32,
    cache: canvasPoolCache,
    colorSpace,
    width: DIMENSIONS,
    height: DIMENSIONS,
    getSortOrder() {
      return curOrder;
    },
    ignoreIntersection(target) {
      if (target.dataset.placeholder === "true") {
        return true;
      }
    },
    visibilityChange(target, visible) {
      const tokenId = parseInt(target.dataset.index, 10);
      if (isFinite(tokenId)) {
        if (visible) visibleTokenIDs.add(tokenId);
        else visibleTokenIDs.delete(tokenId);
      }
    },
    afterIntersection: () => updateRange(),
  });

  const poolAction = pool.action;

  onMount(() => {
    updateTokenCount();
    return () => {
      pool.destroy();
    };
  });

  function onChange(newMemory) {
    // console.log("GOT MEMORY", newMemory);

    // const mem = getStore(address, curOrder);
    if (newMemory.scrollTop != null) {
      // document.documentElement.scrollTop = newMemory.scrollTop;
      // console.log(newMemory.scrollTop);
    }
    const data = get(newMemory.store);
    if (data.length == 0) {
      loadNext();
    } else {
      loading = false;
      firstRun = false;
    }
  }

  async function updateTokenCount() {
    curTokenCount = await fetchOpenEditionTokenCount();
  }

  async function loadNext() {
    if (!curState) {
      return console.warn("no curState in gallery");
    }
    loading = true;
    const offset = curState.offset;
    const page = await fetchTokens({
      offset,
      length: ITEMS_PER_PAGE,
      publisher: address,
      order: curState.order || "asc",
    });
    curState.offset += page.length;
    curState.store.update((data) => {
      const newData = data.slice();
      page.forEach((p) => newData.push(p));
      return newData;
    });
    if (page.length === 0) {
      isEmpty = true;
    }
    // console.log("LOAD", page, isEmpty);
    curMinResult = 0;
    firstRun = false;
    loading = false;
  }

  function updateRange() {
    const idSet = Array.from(visibleTokenIDs);
    if (idSet.length == 0) {
      minRange = null;
      maxRange = null;
    } else {
      const min = idSet.reduce((a, b) => Math.min(a, b));
      const max = idSet.reduce((a, b) => Math.max(a, b));
      minRange = min;
      maxRange = max;
    }
    if (minRange !== lastMinRange || maxRange !== lastMaxRange) {
      if (gallery) {
        gallery.range.update(() => {
          return minRange == null || maxRange == null
            ? null
            : [minRange, maxRange];
        });
      }
      lastMinRange = minRange;
      lastMaxRange = maxRange;
    }
  }

  function padResults(res, minCount = 16) {
    if (!res) res = [];
    if (res.length < minCount) {
      res = res.slice();
      for (let i = res.length; i < minCount; i++) {
        res.push({
          seed: EMPTY_ENCODING_HEX,
          placeholder: true,
          token_id: "placeholder-" + i,
        });
      }
    }

    return res;
  }
</script>

<div class="container" class:compact={$compact}>
  <div class="info noselect">
    <div class="left">
      {#if address}
        TOKENS MINTED BY
        <UserLink {address} bold resolve link={false} addressAsPlaceholder short
        ></UserLink>
      {:else if curTokenCount == null}
        ALL MINTED TOKENS
      {:else}
        ALL {curTokenCount} MINTED TOKENS
      {/if}{#if CONTRACT_NETWORK === "sepolia"}
        (SEPOLIA TESTNET){/if}
    </div>
    <div class="right">
      SORT: <button
        class="sort-button"
        on:click={(ev) => {
          ev.preventDefault();
          orderStore.update((d) => (d === "asc" ? "desc" : "asc"));
        }}>{curOrder == "asc" ? "EARLIEST ↑" : "LATEST ↓"}</button
      >
    </div>
  </div>
  {#if !firstRun && curDataList && curDataList.length === 0 && curMinResult === 0}
    <div class="no-tokens">
      No tokens found — newly minted tokens may take a few minutes to appear in
      the public gallery.
    </div>
  {/if}
  {#key curOrder}
    <div class="grid noselect">
      {#each padResults(curDataList, curMinResult) as row, i (row.token_id)}
        <a
          use:link
          href={row.placeholder ? "" : `/gallery/token/${row.token_id}`}
        >
          <div
            use:poolAction={{ index: i }}
            data-address={address}
            data-encoding={row.seed}
            data-index={row.token_id}
            data-placeholder={row.placeholder}
            role="none"
            class="cell"
            class:valid={!row.placeholder}
            style={`background-color: ${curBackground};`}
            on:click={(ev) => {
              const top = document.documentElement.scrollTop;
              // if (curState) curState.scrollTop = top;
            }}
          ></div>
        </a>
      {/each}
    </div>
  {/key}
  <div class="button-bar">
    {#if !isEmpty && !firstRun}<Button
        text="Load More"
        enabled={!loading}
        on:click={loadNext}
      ></Button>{/if}
  </div>
</div>
<Footer></Footer>

<style>
  .container {
    width: 100%;
    max-width: var(--max-width);
    padding: 40px;
    box-sizing: border-box;
    padding-bottom: 0;
    --size: 32px;
    --gap: 40px;
    --columns: 3;
  }

  .grid {
    width: 100%;
    overflow-y: auto;
    flex-grow: 1;
    overflow-x: hidden;
    grid-template-columns: repeat(var(--columns), minmax(var(--size), 1fr));

    display: grid;
    /* grid-template-columns: repeat(auto-fit, minmax(64px, 1fr)); */
    /* height: 100%; */
    /* grid-template-columns: repeat(3, minmax(0, 1fr)); */

    /* grid-template: repeat(3, 30vw) / repeat(3, 30vw);
    grid-gap: 10px; */
    /* grid-template-columns: 1fr 1fr 1fr 1fr; */
    /* grid-template-columns: 1fr 1fr 1fr; */
    /* grid-template-rows: 1fr 1fr 1fr; */
    grid-gap: var(--gap);
    padding: 0px;
    box-sizing: border-box;
  }

  .container.compact {
    padding: 20px;
    padding-bottom: 0;
    --gap: 20px;
    --columns: 5;
  }

  .cell {
    aspect-ratio: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    cursor: pointer;
    /* position: relative; */
  }

  .button-bar {
    width: 100%;
    margin-top: 40px;
    margin-bottom: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .info,
  .no-tokens {
    /* font-style: italic; */
    padding-bottom: 20px;
    font-size: 12px;
    /* text-transform: uppercase; */
    /* width: 100%; */
    justify-content: flex-start;
    display: flex;
    align-items: flex-start;
  }
  .info {
    justify-content: space-between;
  }
  .no-tokens {
    /* color: tomato; */
    font-style: italic;
  }
  .sort-button {
    appearance: none;
    /* padding: 5px 10px; */
    padding: 0;
    margin: 0;
    background: none;
    border: none;
    border-radius: 0;

    text-decoration: none;
    --color: currentColor;
    --shadow-color: currentColor;
    color: var(--color);
    cursor: default;
    margin: 0;
    text-decoration: none;
    appearance: none;
    border-bottom: 0;
    box-sizing: border-box;
    cursor: pointer;
    outline: 0;
    border-bottom: 1px solid var(--shadow-color);
  }

  @media screen and (max-width: 720px) {
    .container,
    .container.compact {
      padding: 20px;
      padding-bottom: 0;
      --gap: 20px;
      --columns: 2;
    }
  }
  @media screen and (max-width: 720px) {
    .button-bar {
      /* margin-bottom: 40px; */
    }
  }
  @media screen and (max-width: 900px) {
    .container,
    .container.compact {
      padding: 20px;
      padding-bottom: 0;
      --gap: 20px;
      /* --columns: 2; */
    }
  }
</style>
